/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';
import styles from '../../../styles/components/pageComponents/program/ProgramCard.module.css';
import { ReactComponent as IcfLogo } from '../../../assets/icons/IcfLogo.svg';
import { ProgramData } from '../../../types/program';

interface ProgramCardProps {
    program: ProgramData;
    onViewClick: (id: string | number) => void;
    customClass?: string;
    showMenu?: boolean;
    onEnrollLearners?: () => void;
    banner?: 'Owner' | 'Faculty';
}

const formatCertification = (cert?: string, maxLength = 16) => {
    if (!cert?.trim()) return '--';

    const value = cert.trim();

    // If already short enough, don't abbreviate
    if (value.length <= maxLength) {
        return value;
    }

    const ignoreWords = new Set([
        'and',
        'of',
        'the',
        'for',
        'to',
        'in',
        'on',
        'at',
        'with',
        '&',
    ]);

    const abbreviation = value
        .split(/\s+/)
        .filter(word => !ignoreWords.has(word.toLowerCase()))
        .map(word => word[0].toUpperCase())
        .join('');

    return abbreviation || value;
};

const ProgramCard: React.FC<ProgramCardProps> = ({
    program,
    onViewClick,
    customClass = '',
    showMenu = false,
    onEnrollLearners,
    banner,
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const getDuration = (start?: string | Date, end?: string | Date) => {
        if (!start || !end) return '--';

        const startDate = new Date(start);
        const endDate = new Date(end);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return '--';

        const diffTime = endDate.getTime() - startDate.getTime();

        if (diffTime <= 0) return '--';

        const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        const months = Math.floor(totalDays / 30);
        const days = totalDays % 30;

        if (months > 0 && days > 0) return `${months}Month ${days}Days`;
        if (months > 0) return `${months}Months`;
        return `${days}Days`;
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () =>
            document.removeEventListener(
                'mousedown',
                handleClickOutside
            );
    }, []);

    return (
        <div className={`${styles.cardContainer} ${customClass}`}>
            {/* Header Image Section */}
            <div className={styles.imageWrapper}>
                <img
                    src={program.imageUrl}
                    alt={program.title}
                    className={styles.cardImage}
                />

                {banner && (
                    <div className={styles.roleBanner}>
                        {banner}
                    </div>
                )}

                <div className={styles.icfLogo}>
                    <IcfLogo />
                </div>
            </div>

            {/* Content Section */}
            <div className={styles.contentBody}>
                <div className={styles.categoryRow}>
                    <div className={styles.iconBox}>
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#802868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                            <path d="M6 12v5c3 3 9 3 12 0v-5" />
                        </svg>
                    </div>

                    <span className={styles.categoryText}>
                        {program.solutiontitle}
                    </span>

                    {showMenu && (
                        <div
                            className={styles.menuContainer}
                            ref={menuRef}
                        >
                            <button
                                className={styles.moreOptionsButton}
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                aria-label="More options"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="#828282"
                                >
                                    <circle cx="5" cy="12" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="19" cy="12" r="2" />
                                </svg>
                            </button>

                            {isMenuOpen && (
                                <div className={styles.dropdownMenu}>
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => {
                                            setIsMenuOpen(false);
                                            onEnrollLearners?.();
                                        }}
                                    >
                                        Enroll Learners
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <h3 className={styles.programTitle}>{program.title}</h3>

                <div className={styles.metaGrid}>
                    <div className={styles.metaItem}>
                        {program.programformat}
                    </div>

                    <div className={styles.metaItem}>
                        {formatCertification(program.certification)}
                    </div>

                    <div className={styles.metaItem}>
                        {getDuration(program.startdate, program.enddate)}
                    </div>
                </div>

                <div className={styles.buttonGroup}>
                    <button
                        className={styles.viewButton}
                        onClick={() => onViewClick(program.id)}
                    >
                        View Program
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProgramCard;